import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RGB, Icon, ICONS, CopyButton, ConfirmButton, errMsg, timeAgo } from './extUi';
import ExtTeamsCard from './ExtTeamsCard';
import './tournaments.css';

const EVENT_TYPES = ['round_start', 'round_end', 'kill', 'bomb_plant', 'bomb_defuse', 'bomb_explode', 'bomb_defuse_start', 'clutch_start', 'clutch_won', 'clutch_lost', 'zeus_kill', 'knife_kill', 'ace', 'map_point', 'match_point',
  'pause_start', 'pause_end', 'halftime_start', 'halftime_end', 'side_switch', 'knife_winner', 'knife_decision', 'map_start', 'map_end', 'match_end', 'phase_change', 'score_update', 'round_restored', 'map_reset'];

// match phase / status → colour of the state word
const PHASE_TONE = { live: 'ok', upcoming: 'info', finished: 'off', cancelled: 'bad', warmup: 'warn', paused: 'warn', halftime: 'info', knife: 'cyan' };

function parseRosterText(text) {
  return String(text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => {
    const [steam, ...rest] = l.split(/[\s,;]+/);
    return { steam_id: steam, name: rest.join(' ') || null };
  });
}

function MatchForm({ tournamentId, onCreated }) {
  const { t } = useTranslation();
  const blank = { team1_name: '', team1_tag: '', team1_aliases: '', team2_name: '', team2_tag: '', team2_aliases: '', best_of: 1, scheduled_at: '', roster1: '', roster2: '' };
  const [f, setF] = useState(blank);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await axios.post(`/api/v1/ext/tournaments/${tournamentId}/matches`, {
        team1_name: f.team1_name, team1_tag: f.team1_tag || null, team1_aliases: f.team1_aliases,
        team2_name: f.team2_name, team2_tag: f.team2_tag || null, team2_aliases: f.team2_aliases,
        best_of: Number(f.best_of), scheduled_at: f.scheduled_at ? new Date(f.scheduled_at).toISOString() : null,
        roster: { team1: parseRosterText(f.roster1), team2: parseRosterText(f.roster2) },
      });
      setF(blank); setOpen(false); onCreated();
    } catch (err) { setError(errMsg(err, t('ext.createFailed', 'Failed to create'))); }
    finally { setBusy(false); }
  };
  if (!open) return <div><button type="button" className="xt-btn primary" onClick={() => setOpen(true)}><Icon d={ICONS.plus} /> {t('ext.newMatchManual', 'New match (without veto)')}</button></div>;
  const teamCol = (n) => (
    <div className="xt-teamcol">
      <h3>{t('ext.teamN', 'Team {{n}}', { n })}</h3>
      <label className="xt-field"><span className="xt-label">{t('ext.teamName', 'Name')}</span><input required className="xt-input full" value={f[`team${n}_name`]} onChange={set(`team${n}_name`)} maxLength={120} /></label>
      <div className="xt-pair">
        <label className="xt-field"><span className="xt-label">{t('ext.teamTag', 'Tag')}</span><input className="xt-input full" value={f[`team${n}_tag`]} onChange={set(`team${n}_tag`)} maxLength={20} /></label>
        <label className="xt-field"><span className="xt-label">{t('ext.aliases', 'Other names in the server (comma separated)')}</span><input className="xt-input full" value={f[`team${n}_aliases`]} onChange={set(`team${n}_aliases`)} /></label>
      </div>
      <label className="xt-field">
        <span className="xt-label">{t('ext.rosterOptional', 'Roster (optional): one Steam ID per line, optionally followed by the name')}</span>
        <textarea className="xt-input full" value={f[`roster${n}`]} onChange={set(`roster${n}`)} placeholder="76561198000000001 Player1" />
      </label>
    </div>
  );
  return (
    <form onSubmit={submit} className="xt-newmatch">
      <div className="xt-teamcols">{teamCol(1)}{teamCol(2)}</div>
      <div className="xt-form">
        <label className="xt-field"><span className="xt-label">{t('ext.bestOf', 'Format')}</span>
          <select className="xt-input num" value={f.best_of} onChange={set('best_of')}>
            <option value={1}>BO1</option><option value={3}>BO3</option><option value={5}>BO5</option>
          </select>
        </label>
        <label className="xt-field"><span className="xt-label">{t('ext.scheduledAt', 'Scheduled (optional)')}</span><input type="datetime-local" className="xt-input date" value={f.scheduled_at} onChange={set('scheduled_at')} /></label>
        <span className="xt-actions end">
          <button type="button" className="xt-btn" onClick={() => setOpen(false)}>{t('common.cancel', 'Cancel')}</button>
          <button type="submit" className="xt-btn primary" disabled={busy}>{busy ? t('common.loading', 'Loading...') : t('ext.createMatch', 'Create match')}</button>
        </span>
      </div>
      {error && <div className="xt-msg bad">{error}</div>}
    </form>
  );
}

function VetoLink({ tn, onChanged, teamCount }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { const r = await axios.get(`/api/v1/ext/tournaments/${tn.id}/veto-link`); setInfo(r.data); } catch (e) { setErr(errMsg(e, t('ext.loadFailed', 'Failed to load'))); }
  }, [tn.id, t]);
  useEffect(() => { load(); }, [load, tn.veto_format, teamCount]);
  const setFormat = async (f) => {
    setErr('');
    try { await axios.patch(`/api/v1/ext/tournaments/${tn.id}`, { veto_format: f }); onChanged(); } catch (e) { setErr(errMsg(e, t('ext.actionFailed', 'Action failed'))); }
  };
  // built from this page's own address: the server can't always know the public URL behind a proxy
  const url = window.location.origin + ((info && info.path) || tn.veto_path || '');
  return (
    <section className="xt-sec">
      <div className="xt-sec-head"><h2>{t('ext.vetoLink', 'Veto link')}</h2></div>
      <p className="xt-hint">{t('ext.vetoLinkHint', 'One link for the whole tournament, for admins only. Open it at the event, pick the two teams and the format, and enter the veto as it happens. The match is created when you submit. Anyone with the link can create matches, so don’t share it.')}</p>
      <div className="xt-field">
        <span className="xt-label">{t('ext.vetoFormat', 'Veto format')}</span>
        <div className="xt-choices">
          {[['vrs', t('ext.vetoFormatVrs', 'VRS veto'), t('ext.vetoFormatVrsHint', 'Valve rules: sides on the decider are picked')], ['faceit', t('ext.vetoFormatFaceit', 'FACEIT veto'), t('ext.vetoFormatFaceitHint', 'Knife round for sides on the decider')]].map(([k, name, hint]) => (
            <button key={k} type="button" className={`xt-choice${tn.veto_format === k ? ' on' : ''}`} aria-pressed={tn.veto_format === k} onClick={() => tn.veto_format !== k && setFormat(k)}>
              <b>{name}</b>
              <span><span>{hint}</span><span>BO1 / BO3 / BO5</span></span>
            </button>
          ))}
        </div>
      </div>
      <div className="xt-cmd">
        <pre className="xt-code">{url}</pre>
        <span className="xt-actions">
          <CopyButton text={url} />
          <a href={url} target="_blank" rel="noopener noreferrer" className="xt-btn"><Icon d={ICONS.link} /> {t('ext.openVeto', 'Open')}</a>
        </span>
      </div>
      {/* `ready` is false for missing maps OR fewer than 2 teams - show the actual reason */}
      {info && info.problem === 'not_enough_maps' && <div className="xt-msg warn">{t('ext.vetoNeedsMaps', 'Add 7 Active Duty maps on the Maps page - the veto uses your Active Duty pool.')}</div>}
      {info && info.problem === 'not_enough_teams' && <div className="xt-msg warn">{t('ext.vetoNeedsTeams', 'Add at least two teams (Teams section above) - the veto picks from this tournament’s teams.')}</div>}
      {info && info.problem !== 'not_enough_maps' && info.maps && <div className="xt-small">{t('ext.vetoMaps', 'Map pool: {{maps}}', { maps: info.maps.map(m => m.replace(/^de_/, '')).join(', ') })}</div>}
      <div>
        <ConfirmButton label={t('ext.vetoRegenerate', 'New link')} icon={ICONS.refresh} color={RGB.amber}
          warning={t('ext.vetoRegenerateWarning', 'The old link stops working immediately, including vetoes that are open but not submitted yet.')}
          onConfirm={async () => { const r = await axios.post(`/api/v1/ext/tournaments/${tn.id}/veto-link/regenerate`); setInfo({ ...(info || {}), ...r.data }); onChanged(); }} />
      </div>
      {err && <div className="xt-msg bad">{err}</div>}
    </section>
  );
}

function MatchRules({ tn, onChanged }) {
  const { t } = useTranslation();
  const r = tn.rules || {};
  const [f, setF] = useState({ max_rounds: r.max_rounds ?? '', overtime_enabled: r.overtime_enabled == null ? '' : (r.overtime_enabled ? '1' : '0'), ot_max_rounds: r.ot_max_rounds ?? '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const dirty = String(f.max_rounds) !== String(r.max_rounds ?? '') || f.overtime_enabled !== (r.overtime_enabled == null ? '' : (r.overtime_enabled ? '1' : '0')) || String(f.ot_max_rounds) !== String(r.ot_max_rounds ?? '');
  const save = async (e) => {
    e.preventDefault(); setErr(''); setMsg('');
    try {
      await axios.patch(`/api/v1/ext/tournaments/${tn.id}`, { rules: {
        max_rounds: f.max_rounds === '' ? null : Number(f.max_rounds),
        overtime_enabled: f.overtime_enabled === '' ? null : f.overtime_enabled === '1',
        ot_max_rounds: f.ot_max_rounds === '' ? null : Number(f.ot_max_rounds),
      } });
      setMsg(t('ext.rulesSaved', 'Saved. Live matches use the new rules from the next round.'));
      onChanged();
    } catch (e2) { setErr(errMsg(e2, t('ext.actionFailed', 'Action failed'))); }
  };
  return (
    <section className="xt-sec">
      <div className="xt-sec-head"><h2>{t('ext.rulesTitle', 'Match rules')}</h2></div>
      <p className="xt-hint">{t('ext.rulesHint', 'Used to work out map point and match point. If a server prints its own settings in the logs (mp_maxrounds, mp_overtime_enable, mp_overtime_maxrounds), those win for that match. Leave a field empty to use the CS2 default.')}</p>
      <form onSubmit={save} className="xt-form">
        <label className="xt-field">
          <span className="xt-label">{t('ext.rulesMaxRounds', 'Max rounds')}</span>
          <input type="number" min={2} max={60} step={2} placeholder="24" className="xt-input num" value={f.max_rounds} onChange={e => setF({ ...f, max_rounds: e.target.value })} />
        </label>
        <label className="xt-field">
          <span className="xt-label">{t('ext.rulesOvertime', 'Overtime')}</span>
          <select className="xt-input mid" value={f.overtime_enabled} onChange={e => setF({ ...f, overtime_enabled: e.target.value })}>
            <option value="">{t('ext.rulesDefaultYes', 'Default (yes)')}</option>
            <option value="1">{t('ext.rulesYes', 'Yes')}</option>
            <option value="0">{t('ext.rulesNo', 'No')}</option>
          </select>
        </label>
        <label className="xt-field">
          <span className="xt-label">{t('ext.rulesOtMaxRounds', 'Overtime max rounds')}</span>
          <input type="number" min={2} max={60} step={2} placeholder="6" className="xt-input num" disabled={f.overtime_enabled === '0'} value={f.ot_max_rounds} onChange={e => setF({ ...f, ot_max_rounds: e.target.value })} />
        </label>
        <button type="submit" className="xt-btn primary" disabled={!dirty}>{t('common.save', 'Save')}</button>
      </form>
      <div className="xt-small">
        {(() => {
          const mr = Number(f.max_rounds) || 24, om = Number(f.ot_max_rounds) || 6, otOn = f.overtime_enabled !== '0';
          return otOn
            ? t('ext.rulesExplain', 'First to {{win}} wins the map; map point at {{point}}. At {{tie}}-{{tie}} overtime starts: first to {{otWin}} (map point at {{otPoint}}), then +{{step}} per overtime.',
              { win: mr / 2 + 1, point: mr / 2, tie: mr / 2, otWin: mr / 2 + om / 2 + 1, otPoint: mr / 2 + om / 2, step: om / 2 })
            : t('ext.rulesExplainNoOt', 'First to {{win}} wins the map; map point at {{point}}. {{tie}}-{{tie}} is a draw.', { win: mr / 2 + 1, point: mr / 2, tie: mr / 2 });
        })()}
      </div>
      {msg && <div className="xt-msg ok">{msg}</div>}
      {err && <div className="xt-msg bad">{err}</div>}
    </section>
  );
}

function NotificationRow({ tournamentId, n, matches, onDone }) {
  const { t } = useTranslation();
  const [err, setErr] = useState('');
  const names = n.payload && n.payload.names;
  const act = async (body) => {
    setErr('');
    try { await axios.post(`/api/v1/ext/tournaments/${tournamentId}/notifications/${n.id}/resolve`, body); onDone(); }
    catch (e) { setErr(errMsg(e, t('ext.actionFailed', 'Action failed'))); }
  };
  const vs = names && (names.CT || names.T) ? `${names.CT || '?'} vs ${names.T || '?'}` : null;
  const text = {
    auto_attached: t('ext.notifAutoAttached', 'Server attached automatically to {{match}}', { match: (matches.find(m => m.id === n.match_id) || {}).label || `#${n.match_id}` }),
    needs_attach: t('ext.notifNeedsAttach', 'A server is playing {{vs}} - choose the match', { vs: vs || '?' }),
    unknown_match: t('ext.notifUnknown', 'A server is playing {{vs}}, which matches no match of this tournament', { vs: vs || '?' }),
    match_changed: t('ext.notifChanged', 'An attached server now plays {{vs}} - is it still the same match?', { vs: vs || '?' }),
  }[n.type] || n.type;
  const cands = (n.payload && n.payload.candidates) || [];
  return (
    <div className="xt-row">
      <div className="xt-row-top">
        <span className={`xt-state ${n.type === 'auto_attached' ? 'ok' : 'warn'}`}><Icon d={ICONS.bell} /></span>
        <span className="xt-row-main xt-row-name">{text}</span>
        <span className="xt-small">{timeAgo(n.created_at)}</span>
      </div>
      <div className="xt-actions">
        {n.type !== 'auto_attached' && cands.slice(0, 4).map(c => (
          <button key={c.match_id} type="button" className={`xt-btn${c.confident ? ' primary' : ''}`} onClick={() => act({ action: 'attach', match_id: c.match_id })}>
            <Icon d={ICONS.link} /> {t('ext.attachTo', 'Attach to {{match}}', { match: c.label })}
          </button>
        ))}
        {n.type !== 'auto_attached' && !cands.length && n.source_id && (
          <select className="xt-input small" aria-label={t('ext.attachToMatch', 'Attach to match…')} defaultValue="" onChange={e => e.target.value && act({ action: 'attach', match_id: Number(e.target.value) })}>
            <option value="">{t('ext.attachToMatch', 'Attach to match…')}</option>
            {matches.filter(m => m.status !== 'finished' && m.status !== 'cancelled').map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        )}
        {n.type === 'auto_attached' && <button type="button" className="xt-btn warn" onClick={() => act({ action: 'undo' })}><Icon d={ICONS.unlink} /> {t('ext.undo', 'Undo')}</button>}
        <button type="button" className="xt-btn" onClick={() => act({ action: 'dismiss' })}>{n.type === 'auto_attached' ? t('ext.ok', 'OK') : t('ext.dismiss', 'Dismiss')}</button>
      </div>
      {err && <div className="xt-msg bad">{err}</div>}
    </div>
  );
}

function SourceRow({ tournamentId, s, matches, onDone }) {
  const { t } = useTranslation();
  const [target, setTarget] = useState('');
  const [from, setFrom] = useState('map_start');
  const [err, setErr] = useState('');
  const attached = matches.find(m => m.id === s.current_match_id);
  const attach = async () => {
    setErr('');
    try { await axios.post(`/api/v1/ext/tournaments/${tournamentId}/sources/${s.id}/attach`, { match_id: Number(target), from }); setTarget(''); onDone(); }
    catch (e) { setErr(errMsg(e, t('ext.actionFailed', 'Action failed'))); }
  };
  return (
    <div className="xt-row">
      <div className="xt-row-top">
        <div className="xt-row-main">
          <div className="xt-row-name">{s.hostname || s.label || s.remote_ip}</div>
          <div className="xt-meta mono">
            <span>{s.remote_ip}</span>
            <span>{t('ext.identifiedBy', 'identified by {{how}}', { how: s.identified_by === 'ip' ? t('ext.byIp', 'IP address') : s.identified_by })}</span>
            <span>{t('ext.lastSeen', 'last seen {{when}}', { when: timeAgo(s.last_seen) })}</span>
          </div>
        </div>
        <span className={`xt-state ${s.live ? 'ok' : 'off'}`}>{s.live ? t('ext.live', 'Live') : t('ext.idle', 'Idle')}</span>
        {attached
          ? <Link to={`/admin/tournaments/${attached.tournament_id}/matches/${attached.id}`} className="xt-state ok">{attached.label}</Link>
          : <span className="xt-state warn">{t('ext.unassigned', 'Not attached')}</span>}
      </div>
      <div className="xt-actions">
        <select className="xt-input small" aria-label={attached ? t('ext.moveTo', 'Move to match…') : t('ext.attachToMatch', 'Attach to match…')} value={target} onChange={e => setTarget(e.target.value)}>
          <option value="">{attached ? t('ext.moveTo', 'Move to match…') : t('ext.attachToMatch', 'Attach to match…')}</option>
          {matches.filter(m => m.id !== s.current_match_id && m.status !== 'cancelled' && m.status !== 'finished').map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
        <select className="xt-input small" value={from} onChange={e => setFrom(e.target.value)} aria-label={t('ext.fromHint', 'Which logs of this server belong to the match')} title={t('ext.fromHint', 'Which logs of this server belong to the match')}>
          <option value="map_start">{t('ext.fromMapStart', 'from the last map load')}</option>
          <option value="unassigned">{t('ext.fromUnassigned', 'all logs since last detach')}</option>
          <option value="now">{t('ext.fromNow', 'from now on')}</option>
        </select>
        <button type="button" className="xt-btn primary" disabled={!target} onClick={attach}><Icon d={ICONS.link} /> {attached ? t('ext.move', 'Move') : t('ext.attach', 'Attach')}</button>
        {attached && <ConfirmButton label={t('ext.detach', 'Detach')} icon={ICONS.unlink} color={RGB.amber}
          warning={t('ext.detachWarning', 'New logs from this server stop going to {{match}}', { match: attached.label })}
          onConfirm={async () => { await axios.post(`/api/v1/ext/tournaments/${tournamentId}/sources/${s.id}/detach`, { at: 'now' }); onDone(); }} />}
      </div>
      {err && <div className="xt-msg bad">{err}</div>}
    </div>
  );
}

function Webhooks({ tournamentId }) {
  const { t } = useTranslation();
  const [hooks, setHooks] = useState([]);
  const [url, setUrl] = useState('');
  const [types, setTypes] = useState(['round_end', 'bomb_plant', 'match_end']);
  const [secret, setSecret] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { const r = await axios.get(`/api/v1/ext/tournaments/${tournamentId}/webhooks`); setHooks(r.data.data || []); } catch (_) { }
  }, [tournamentId]);
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);
  const add = async (e) => {
    e.preventDefault(); setErr('');
    try { const r = await axios.post(`/api/v1/ext/tournaments/${tournamentId}/webhooks`, { url, event_types: types }); setSecret({ id: r.data.id, value: r.data.secret }); setUrl(''); load(); }
    catch (e2) { setErr(errMsg(e2, t('ext.actionFailed', 'Action failed'))); }
  };
  const toggleType = (ty) => setTypes(types.includes(ty) ? types.filter(x => x !== ty) : [...types, ty]);
  return (
    <section className="xt-sec">
      <div className="xt-sec-head"><h2>{t('ext.webhooks', 'Webhooks')}</h2>{hooks.length > 0 && <span className="xt-count">{hooks.length}</span>}</div>
      <p className="xt-hint">{t('ext.webhooksHint', 'Each event is POSTed as JSON to your URL, signed with X-Flux-Signature (HMAC-SHA256 of the body with the webhook secret). Failed deliveries are retried for ~13 minutes.')}</p>
      {hooks.length > 0 && (
        <div className="xt-list">
          {hooks.map(h => (
            <div key={h.id} className="xt-row">
              <div className="xt-row-top">
                <span className="xt-url">{h.url}</span>
                {!h.is_active && <span className="xt-state off">{t('ext.paused', 'Paused')}</span>}
                <span className={`xt-state ${h.last_status && h.last_status < 300 ? 'ok' : h.last_status ? 'bad' : 'off'}`}>{h.last_status ? `HTTP ${h.last_status}` : (h.last_error || t('ext.noDeliveries', 'no deliveries yet'))}</span>
              </div>
              <div className="xt-meta">
                <span>{h.event_types.length ? h.event_types.join(', ') : t('ext.allEvents', 'all events')}</span>
                <span>{t('ext.deliveryCounts', '{{d}} delivered, {{p}} pending, {{f}} failed', { d: h.deliveries.delivered, p: h.deliveries.pending, f: h.deliveries.failed })}</span>
              </div>
              {secret && secret.id === h.id && (
                <div className="xt-cmd">
                  <pre className="xt-code">{secret.value}</pre><CopyButton text={secret.value} small />
                </div>
              )}
              <div className="xt-actions">
                <button type="button" className="xt-btn" onClick={async () => { await axios.post(`/api/v1/ext/tournaments/${tournamentId}/webhooks/${h.id}/test`); setTimeout(load, 1500); }}>{t('ext.sendTest', 'Send test')}</button>
                <button type="button" className="xt-btn" onClick={async () => { const r = await axios.get(`/api/v1/ext/tournaments/${tournamentId}/webhooks/${h.id}`, { params: { reveal: 1 } }); setSecret({ id: h.id, value: r.data.secret }); }}>{t('ext.showSecret', 'Show secret')}</button>
                <button type="button" className={`xt-btn ${h.is_active ? 'warn' : 'ok'}`} onClick={async () => { await axios.patch(`/api/v1/ext/tournaments/${tournamentId}/webhooks/${h.id}`, { is_active: !h.is_active }); load(); }}>{h.is_active ? t('ext.pause', 'Pause') : t('ext.resume', 'Resume')}</button>
                {h.deliveries.failed > 0 && <button type="button" className="xt-btn warn" onClick={async () => { await axios.post(`/api/v1/ext/tournaments/${tournamentId}/webhooks/${h.id}/redeliver`, {}); load(); }}>{t('ext.resendFailed', 'Re-send failed')}</button>}
                <ConfirmButton label={t('ext.rotateSecret', 'New secret')} icon={ICONS.refresh} color={RGB.amber} warning={t('ext.rotateSecretWarning', 'The receiver must be updated with the new secret')}
                  onConfirm={async () => { const r = await axios.post(`/api/v1/ext/tournaments/${tournamentId}/webhooks/${h.id}/rotate-secret`); setSecret({ id: h.id, value: r.data.secret }); load(); }} />
                <ConfirmButton label={t('common.delete', 'Delete')} icon={ICONS.trash} warning={t('ext.deleteWebhookWarning', 'Stops all deliveries to this URL')}
                  onConfirm={async () => { await axios.delete(`/api/v1/ext/tournaments/${tournamentId}/webhooks/${h.id}`); load(); }} />
              </div>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={add} className="xt-field">
        <span className="xt-label">{t('ext.webhookUrl', 'Add a webhook URL')}</span>
        <div className="xt-form">
          <input className="xt-input wide" aria-label={t('ext.webhookUrl', 'Add a webhook URL')} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://stage-control.example.com/flux" />
          <button type="submit" className="xt-btn primary" disabled={!url}><Icon d={ICONS.plus} /> {t('ext.add', 'Add')}</button>
        </div>
        <div className="xt-types">
          {EVENT_TYPES.map(ty => (
            <button key={ty} type="button" className={`xt-type${types.includes(ty) ? ' on' : ''}`} aria-pressed={types.includes(ty)} onClick={() => toggleType(ty)}>{ty}</button>
          ))}
        </div>
        <div className="xt-small">{t('ext.eventTypesHint', 'Selected event types are sent. Select none to send every event.')}</div>
        {err && <div className="xt-msg bad">{err}</div>}
      </form>
    </section>
  );
}

export default function ExtTournamentDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [tn, setTn] = useState(null);
  const [matches, setMatches] = useState([]);
  const [sources, setSources] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [error, setError] = useState('');
  const [editName, setEditName] = useState(null);
  const [teamCount, setTeamCount] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b, c, d] = await Promise.all([
        axios.get(`/api/v1/ext/tournaments/${id}`),
        axios.get(`/api/v1/ext/tournaments/${id}/matches`),
        axios.get(`/api/v1/ext/tournaments/${id}/sources`),
        axios.get(`/api/v1/ext/tournaments/${id}/notifications`),
      ]);
      setTn(a.data);
      setMatches((b.data.data || []).map(m => ({ ...m, label: `${m.teams.team1.name} vs ${m.teams.team2.name}` })));
      setSources(c.data.data || []);
      setNotifs(d.data.data || []);
      setError('');
    } catch (e) { setError(errMsg(e, t('ext.loadFailed', 'Failed to load'))); }
  }, [id, t]);

  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [load]);

  if (!tn) return <div className="xt-page"><div className="xt-empty">{error || t('common.loading', 'Loading...')}</div></div>;

  const patch = async (body) => { await axios.patch(`/api/v1/ext/tournaments/${id}`, body); load(); };

  return (
    <div className="xt-page">
      <header>
        <Link to="/admin/tournaments" className="xt-back"><Icon d={ICONS.back} /> {t('ext.allTournaments', 'All tournaments')}</Link>
        <div className="xt-head">
          {editName == null ? (
            <>
              <h1 className="xt-title">{tn.name}</h1>
              <button type="button" className="xt-btn icon" onClick={() => setEditName(tn.name)} title={t('ext.rename', 'Rename')} aria-label={t('ext.rename', 'Rename')}><Icon d={ICONS.edit} /></button>
            </>
          ) : (
            <form onSubmit={async (e) => { e.preventDefault(); await patch({ name: editName }); setEditName(null); }} className="xt-form">
              <input className="xt-input wide" aria-label={t('ext.rename', 'Rename')} value={editName} onChange={e => setEditName(e.target.value)} autoFocus maxLength={120} />
              <button type="submit" className="xt-btn primary">{t('common.save', 'Save')}</button>
              <button type="button" className="xt-btn" onClick={() => setEditName(null)}>{t('common.cancel', 'Cancel')}</button>
            </form>
          )}
          {tn.archived && <span className="xt-state off">{t('ext.archived', 'Archived')}</span>}
          <span className="grow" />
          <Link to={`/admin/tournaments/${tn.id}/stats`} className="xt-btn">{t('extStats.open', 'Player & team stats')}</Link>
        </div>
      </header>

      {/* Log address */}
      <section className="xt-sec">
        <div className="xt-sec-head"><h2>{t('ext.logAddress', 'Log address')}</h2></div>
        <p className="xt-hint">{t('ext.logAddressHint', 'Run this command on every game server of the tournament (or put it in their server config). All servers use the same address; FluxBot tells them apart and assigns each to its match.')}</p>
        <div className="xt-cmd">
          <pre className="xt-code">{tn.logaddress_command}</pre>
          <CopyButton text={tn.logaddress_command} />
        </div>
        <div className="xt-actions">
          <label className="xt-check">
            <input type="checkbox" checked={tn.auto_attach} onChange={e => patch({ auto_attach: e.target.checked })} />
            {t('ext.autoAttach', 'Attach servers to matches automatically (by team names / roster)')}
          </label>
          <span className="xt-actions end">
            <ConfirmButton label={t('ext.rotateAddress', 'Rotate address')} icon={ICONS.refresh} color={RGB.amber}
              warning={t('ext.rotateWarning', 'The old address stops working immediately. Every server must run the new command.')}
              onConfirm={async () => { await axios.post(`/api/v1/ext/tournaments/${id}/rotate-log-address`); load(); }} />
            {!tn.archived
              ? <ConfirmButton label={t('ext.archive', 'Archive')} icon={ICONS.trash} warning={t('ext.archiveWarning', 'The log address stops accepting logs. All data is kept.')}
                  onConfirm={async () => { await axios.delete(`/api/v1/ext/tournaments/${id}`); navigate('/admin/tournaments'); }} />
              : <button type="button" className="xt-btn ok" onClick={() => patch({ archived: false })}>{t('ext.unarchive', 'Unarchive')}</button>}
          </span>
        </div>
      </section>

      {/* Notifications */}
      {notifs.length > 0 && (
        <section className="xt-sec attention">
          <div className="xt-sec-head"><h2>{t('ext.notifications', 'Needs your attention')}</h2><span className="xt-count warn">{notifs.length}</span></div>
          <p className="xt-hint">{t('ext.notificationsHint', 'Servers that could not be matched automatically, and automatic attachments you can confirm or undo.')}</p>
          <div className="xt-list">
            {notifs.map(n => <NotificationRow key={n.id} tournamentId={id} n={n} matches={matches} onDone={load} />)}
          </div>
        </section>
      )}

      <ExtTeamsCard tournamentId={id} onCount={setTeamCount} />
      <VetoLink tn={tn} onChanged={load} teamCount={teamCount} />
      <MatchRules key={JSON.stringify(tn.rules)} tn={tn} onChanged={load} />

      {/* Matches */}
      <section className="xt-sec">
        <div className="xt-sec-head"><h2>{t('ext.matches', 'Matches')}</h2>{matches.length > 0 && <span className="xt-count">{matches.length}</span>}</div>
        <MatchForm tournamentId={id} onCreated={load} />
        {matches.length === 0 && <div className="xt-empty">{t('ext.noMatches', 'No matches yet. Do the veto on the veto link (it creates the match), or create a match by hand before it starts; the logs are assigned to matches by team names.')}</div>}
        {matches.length > 0 && (
          <div className="xt-list">
            {matches.map(m => (
              <Link key={m.id} to={`/admin/tournaments/${id}/matches/${m.id}`} className="xt-row">
                <div className="xt-row-top">
                  <span className={`xt-state ${PHASE_TONE[m.phase] || PHASE_TONE[m.status] || 'off'}`}>{t(`ext.phase.${m.phase}`, m.phase)}</span>
                  <div className="xt-row-main">
                    <div className="xt-row-name">{m.teams.team1.name} <span className="vs">vs</span> {m.teams.team2.name}</div>
                    <div className="xt-meta">
                      <span>BO{m.best_of}</span>
                      {m.veto && <span title={m.veto.maps.map(x => x.map.replace(/^de_/, '')).join(', ')}>{t('ext.vetoPill', 'Veto')}: {m.veto.maps.map(x => x.map.replace(/^de_/, '')).join(', ')}</span>}
                      {m.sources.length > 0 && <span title={m.sources.map(s => s.hostname || s.remote_ip).join(', ')}>{t('ext.serversAttached', 'Servers: {{count}}', { count: m.sources.length })}</span>}
                    </div>
                  </div>
                  <span className="xt-score">
                    {m.best_of > 1 && <span className="series">{m.series.team1}–{m.series.team2}</span>}
                    {m.status === 'live' ? `${m.teams.team1.score} : ${m.teams.team2.score}` : ''}
                  </span>
                  {/* the row is a link: keep clicks on its controls from navigating */}
                  <span className="xt-actions" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    {m.seating && (
                      <button type="button" className="xt-btn" title={t('extSeating.editSeatingHint', 'Change the stage seating of this match only')}
                        onClick={() => navigate(`/admin/tournaments/${id}/matches/${m.id}/seating`)}>
                        {t('extSeating.editSeating', 'Edit seating')}
                      </button>
                    )}
                    <ConfirmButton label={t('common.delete', 'Delete')} icon={ICONS.trash}
                      warning={t('ext.deleteMatchWarning', 'Deletes the match and its stats for good. Its servers are detached; raw logs are kept.')}
                      onConfirm={async () => { await axios.delete(`/api/v1/ext/tournaments/${id}/matches/${m.id}`, { params: { hard: 1 } }); load(); }} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Sources */}
      <section className="xt-sec">
        <div className="xt-sec-head"><h2>{t('ext.sources', 'Game servers')}</h2>{sources.length > 0 && <span className="xt-count">{sources.length}</span>}</div>
        <p className="xt-hint">{t('ext.sourcesHint', 'Every server that has sent logs to this tournament. Attach a server to a match, or move it to another match if it was assigned wrongly; the affected matches are re-parsed from the stored logs.')}</p>
        {sources.length === 0 && <div className="xt-empty">{t('ext.noSources', 'No logs received yet.')}</div>}
        {sources.length > 0 && (
          <div className="xt-list">
            {sources.map(s => <SourceRow key={s.id} tournamentId={id} s={s} matches={matches.map(m => ({ ...m, tournament_id: Number(id) }))} onDone={load} />)}
          </div>
        )}
      </section>

      <Webhooks tournamentId={id} />

      <section className="xt-sec">
        <div className="xt-sec-head"><h2>{t('ext.apiTitle', 'API')}</h2></div>
        <p className="xt-hint">{t('ext.apiHint', 'All data of this tournament is available with an API key (Admin → API): polling, Server-Sent Events, WebSocket and webhooks. One stream carries every match of the tournament.')}</p>
        <pre className="xt-code">{`curl -N -H "Authorization: Bearer flx_live_…" ${window.location.origin}/api/v1/ext/tournaments/${id}/events/stream\n\nws: ${window.location.origin.replace(/^http/, 'ws')}/ws/ext?token=flx_live_…&tournament=${id}`}</pre>
      </section>
    </div>
  );
}
